import { useEffect, useState } from 'react';
import {
  useCustomMutation,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import { Form, Input, Modal, Radio } from 'antd';
import type {
  NovelistDeliveryConfigurationCreateIn,
  NovelistDeliveryConfigurationOut,
  NovelistDeliveryConfigurationUpdateIn,
} from '@/api/generated/schemas';
import { NovelistDeliveryMethod } from '@/api/generated/schemas';
import { extractBackendMessage } from '@/utils/apiError';
import { toDeliveryAccount } from './publisherPayload';

type Values = {
  method: NovelistDeliveryMethod;
  delivery_account?: string;
};

interface DeliveryConfigurationModalProps {
  open: boolean;
  onClose: () => void;
  publisherId: number;
  /** When set, the modal changes this configuration's account; otherwise it configures delivery. */
  configuration?: NovelistDeliveryConfigurationOut | null;
  onSaved: () => void;
}

export const DeliveryConfigurationModal = ({
  open,
  onClose,
  publisherId,
  configuration,
  onSaved,
}: DeliveryConfigurationModalProps) => {
  const t = useTranslate();
  const { open: notify } = useNotification();
  const { mutateAsync } = useCustomMutation();
  const [form] = Form.useForm<Values>();
  const [submitting, setSubmitting] = useState(false);

  const mode = configuration ? 'account' : 'configure';
  const tk = (suffix: string) =>
    t(`content_catalog.publishers.integration.${mode}.${suffix}`);
  const tf = (field: string) =>
    t(`content_catalog.publishers.integration.fields.${field}`);

  useEffect(() => {
    if (open) {
      form.resetFields();
    } else {
      setSubmitting(false);
    }
  }, [open, configuration, form]);

  const url = `content_catalog/publishers/${publisherId}/delivery_configuration`;

  const handleSubmit = async () => {
    let values: Values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSubmitting(true);
    try {
      const delivery_account = toDeliveryAccount(values.delivery_account);
      if (configuration) {
        const payload: NovelistDeliveryConfigurationUpdateIn = {
          delivery_account,
        };
        await mutateAsync({
          url,
          method: 'patch',
          values: payload,
          successNotification: false,
          errorNotification: false,
        });
      } else {
        const payload: NovelistDeliveryConfigurationCreateIn = {
          method: values.method,
          delivery_account,
        };
        await mutateAsync({
          url,
          method: 'post',
          values: payload,
          successNotification: false,
          errorNotification: false,
        });
      }
      notify?.({
        type: 'success',
        message: t('notifications.success'),
        description: tk('success'),
      });
      onSaved();
      onClose();
    } catch (error) {
      notify?.({
        type: 'error',
        message: t('notifications.error'),
        description: extractBackendMessage(error) ?? tk('error_fallback'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={tk('title')}
      okText={tk('submit')}
      okButtonProps={{ loading: submitting }}
      cancelButtonProps={{ disabled: submitting }}
      closable={!submitting}
      maskClosable={!submitting}
      onCancel={onClose}
      onOk={handleSubmit}
      destroyOnClose
      width={520}
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        disabled={submitting}
        initialValues={{
          method: configuration?.method ?? NovelistDeliveryMethod.ftp,
          delivery_account: configuration?.delivery_account ?? undefined,
        }}
      >
        {!configuration && (
          <Form.Item
            name="method"
            label={tf('method')}
            rules={[{ required: true }]}
            extra={t('content_catalog.publishers.integration.hints.method')}
          >
            <Radio.Group
              optionType="button"
              options={Object.values(NovelistDeliveryMethod).map((value) => ({
                label: t(
                  `content_catalog.publishers.integration.method.${value}`,
                  { defaultValue: value }
                ),
                value,
              }))}
            />
          </Form.Item>
        )}
        <Form.Item
          name="delivery_account"
          label={tf('delivery_account')}
          rules={[
            {
              max: 255,
              message: t(
                'content_catalog.publishers.create.errors.max_length',
                { max: 255 }
              ),
            },
          ]}
          extra={t('content_catalog.publishers.integration.hints.account')}
        >
          <Input allowClear autoFocus />
        </Form.Item>
      </Form>
    </Modal>
  );
};
